/**
 * Map data structure for storing tile-based map information.
 * Handles terrain storage, mine node placement, and map boundaries.
 */

import { TerrainType, isTraversable, isBuildable } from '@shared/constants/terrain';
import { TILE_SIZE, MAP_TILE_WIDTH, MAP_TILE_HEIGHT } from '@shared/constants/map';

/**
 * Map configuration constants.
 */
export const MAP_CONFIG = {
  /** Map dimensions in tiles */
  WIDTH: MAP_TILE_WIDTH,
  HEIGHT: MAP_TILE_HEIGHT,
  /** Tile size in pixels */
  TILE_SIZE,
} as const;

/**
 * Calculate map boundaries in pixels.
 */
export function getMapBounds() {
  return {
    width: MAP_CONFIG.WIDTH * MAP_CONFIG.TILE_SIZE,
    height: MAP_CONFIG.HEIGHT * MAP_CONFIG.TILE_SIZE,
  };
}

/**
 * Mine node placement point (occupies 2x2 tiles).
 */
export interface MineNodePosition {
  /** Top-left tile X coordinate */
  x: number;
  /** Top-left tile Y coordinate */
  y: number;
}

/**
 * Map data structure storing terrain and mine node information.
 */
export class MapData {
  terrain: TerrainType[][]; // Made public for efficient fromJSON
  private mineNodes: MineNodePosition[];
  private readonly width: number;
  private readonly height: number;
  private readonly tileSize: number;

  constructor(
    width: number = MAP_TILE_WIDTH,
    height: number = MAP_TILE_HEIGHT,
    tileSize: number = TILE_SIZE
  ) {
    this.width = width;
    this.height = height;
    this.tileSize = tileSize;
    this.terrain = Array(height)
      .fill(null)
      .map(() => Array(width).fill(TerrainType.GRASS));
    this.mineNodes = [];
  }

  /**
   * Get terrain type at specified tile coordinates.
   */
  getTerrain(x: number, y: number): TerrainType {
    if (!this.isWithinBounds(x, y)) {
      throw new Error(`Coordinates (${x}, ${y}) are out of bounds`);
    }
    return this.terrain[y][x];
  }

  /**
   * Set terrain type at specified tile coordinates.
   */
  setTerrain(x: number, y: number, terrain: TerrainType): void {
    if (!this.isWithinBounds(x, y)) {
      throw new Error(`Coordinates (${x}, ${y}) are out of bounds`);
    }
    this.terrain[y][x] = terrain;
  }

  /**
   * Check if coordinates are within map bounds.
   */
  isWithinBounds(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  /**
   * Get map dimensions.
   */
  getDimensions(): { width: number; height: number } {
    return {
      width: this.width,
      height: this.height,
    };
  }

  /**
   * Get tile size.
   */
  getTileSize(): number {
    return this.tileSize;
  }

  /**
   * Get map boundaries in pixels.
   */
  getBounds(): { width: number; height: number } {
    return {
      width: this.width * this.tileSize,
      height: this.height * this.tileSize,
    };
  }

  /**
   * Add a mine node placement point.
   * Each mine node occupies 2x2 tiles starting at the specified position.
   */
  addMineNode(x: number, y: number): void {
    if (!this.isWithinBounds(x, y) || !this.isWithinBounds(x + 1, y + 1)) {
      throw new Error(`Mine node position (${x}, ${y}) is invalid or out of bounds`);
    }
    this.mineNodes.push({ x, y });
    // Paint the 2x2 area as mine node terrain
    for (let dy = 0; dy < 2; dy++) {
      for (let dx = 0; dx < 2; dx++) {
        this.setTerrain(x + dx, y + dy, TerrainType.MINE_NODE);
      }
    }
  }

  /**
   * Remove a mine node at the specified position.
   * Removes the mine node from the array (does not change terrain).
   */
  removeMineNode(x: number, y: number): void {
    const index = this.mineNodes.findIndex((node) => node.x === x && node.y === y);
    if (index !== -1) {
      this.mineNodes.splice(index, 1);
    }
  }

  /**
   * Get all mine node positions.
   */
  getMineNodes(): readonly MineNodePosition[] {
    return this.mineNodes;
  }

  /**
   * Check if a position is a valid mine node location.
   * A position is valid if it matches the top-left corner of any mine node.
   */
  isMineNodePosition(x: number, y: number): boolean {
    return this.mineNodes.some((node) => node.x === x && node.y === y);
  }

  /**
   * Check if any tiles in the specified area overlap with a mine node.
   * Used for building placement validation.
   */
  overlapsMineNode(tileX: number, tileY: number, width: number, height: number): boolean {
    for (const node of this.mineNodes) {
      // Check if building area overlaps with mine node area (both are 2x2)
      if (
        tileX < node.x + 2 &&
        tileX + width > node.x &&
        tileY < node.y + 2 &&
        tileY + height > node.y
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if a tile at (x, y) is part of any mine node.
   * Mine nodes occupy 2x2 tiles starting at their top-left corner.
   */
  isTilePartOfMineNode(x: number, y: number): boolean {
    for (const node of this.mineNodes) {
      // Check if (x, y) is within the 2x2 area of this mine node
      if (x >= node.x && x < node.x + 2 && y >= node.y && y < node.y + 2) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if terrain at specified coordinates is traversable.
   */
  isTraversable(x: number, y: number): boolean {
    if (!this.isWithinBounds(x, y)) {
      return false;
    }
    const terrain = this.getTerrain(x, y);
    return isTraversable(terrain);
  }

  /**
   * Check if terrain at specified coordinates is buildable.
   */
  isBuildable(x: number, y: number): boolean {
    if (!this.isWithinBounds(x, y)) {
      return false;
    }
    const terrain = this.getTerrain(x, y);
    return isBuildable(terrain);
  }

  /**
   * Serialize map data to JSON for static map storage.
   */
  toJSON(): {
    width: number;
    height: number;
    tileSize: number;
    terrain: TerrainType[][];
    mineNodes: MineNodePosition[];
  } {
    return {
      width: this.width,
      height: this.height,
      tileSize: this.tileSize,
      terrain: this.terrain,
      mineNodes: [...this.mineNodes],
    };
  }

  /**
   * Create MapData instance from JSON data.
   * Optimized to process in chunks to prevent browser hang.
   */
  static async fromJSON(
    data: {
      width: number;
      height: number;
      tileSize: number;
      terrain: TerrainType[][];
      mineNodes: MineNodePosition[];
    },
    onProgress?: (progress: number) => void
  ): Promise<MapData> {
    const map = new MapData(data.width, data.height, data.tileSize);
    const totalTiles = data.width * data.height;
    const chunkSize = 1000; // Process 1000 tiles at a time
    let processedTiles = 0;

    // Validate terrain array structure
    if (!Array.isArray(data.terrain) || data.terrain.length !== data.height) {
      throw new Error(
        `Invalid terrain array: expected ${data.height} rows, got ${data.terrain?.length ?? 0}`
      );
    }

    // Copy terrain data in chunks
    for (let y = 0; y < data.height; y++) {
      const terrainRow = data.terrain[y];
      if (!Array.isArray(terrainRow) || terrainRow.length !== data.width) {
        throw new Error(
          `Invalid terrain row ${y}: expected ${data.width} columns, got ${terrainRow?.length ?? 0}`
        );
      }
      for (let x = 0; x < data.width; x++) {
        map.terrain[y][x] = terrainRow[x] as TerrainType;
        processedTiles++;

        // Yield to browser every chunkSize tiles
        if (processedTiles % chunkSize === 0) {
          await new Promise((resolve) => setTimeout(resolve, 0));
          if (onProgress) {
            onProgress(processedTiles / totalTiles);
          }
        }
      }
    }

    // Copy mine nodes (usually small number, so process all at once)
    for (const node of data.mineNodes) {
      map.addMineNode(node.x, node.y);
    }

    return map;
  }
}
